import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalOrders } from '../hooks/useGlobalOrders';
import { useWorkspace } from './WorkspaceContext';
import { useDrivers } from '../hooks';
import { Customer, Driver, Order } from '../models';
import { OrderStatus } from '../types/order/IOrder';
import { useUserSession } from './UserContext';

interface NotificationContextType {
  draftOrderCount: number;
  activityLog: ActivityLogEntry[];
}

export interface ActivityLogEntry {
  id: string;
  type: 'new_order' | 'order_registered' | 'order_started' | 'order_completed';
  orderId: string;
  orderNumber?: string;
  customerName: string;
  timestamp: Date;
  message: string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Constants for better maintainability
const ACTIVITY_LOG_MAX_ITEMS = 10;
const ACTIVITY_LOG_RETENTION_HOURS = 24;
const STORAGE_PREFIX = 'lgb_notifications';
const LOCALSTORAGE_QUOTA_EXCEEDED = 'QuotaExceededError';

// Helper function to get customer name consistently
const getCustomerName = (customer: Order['customer']): string => {
  return Customer.getCustomerName(customer) || 'Ukjent kunde';
};

// Helper function to get driver name
const getDriverName = (driverId: string | undefined, drivers: any[]): string => {
  if (!driverId) return 'Ukjent sjåfør';
  
  // Look up driver by both email and ID (same logic as useDriversApi)
  const driver = drivers.find(d => 
    d.email === driverId || 
    d.id === driverId
  );
  
  if (!driver) return 'Ukjent sjåfør';

  return Driver.getDriverName(driver, true) || 'Ukjent sjåfør';
};

// Helper function to handle localStorage quota
const safeSetLocalStorage = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error: any) {
    if (error.name === LOCALSTORAGE_QUOTA_EXCEEDED || error.code === 22) {
      console.warn('localStorage quota exceeded, clearing old data:', key);
      try {
        // Try to clear some space by removing old notification data
        const keysToRemove = Object.keys(localStorage).filter(k => 
          k.startsWith(STORAGE_PREFIX) && k !== key
        );
        keysToRemove.forEach(k => localStorage.removeItem(k));
        
        // Try again after cleanup
        localStorage.setItem(key, value);
        return true;
      } catch {
        console.error('Failed to save to localStorage even after cleanup');
        return false;
      }
    }
    console.error('localStorage error:', error);
    return false;
  }
};

// Inner provider that should only be rendered once the user is authenticated.
const AuthenticatedNotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { data: allOrders, isLoading } = useGlobalOrders();
  const { activeWorkspace } = useWorkspace();
  const driversQuery = useDrivers(activeWorkspace?.workspaceId ?? "", false); // Use non-realtime for performance
  const allDrivers = useMemo(() => driversQuery.data ?? [], [driversQuery.data]);
  
  // Page Visibility API - Pause notifications when tab is hidden
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  const previousOrdersRef = useRef<Order[]>([]);
  const workspaceId = activeWorkspace?.workspaceId ?? '';
  
  // Storage keys
  const draftCountKey = useMemo(() => `${STORAGE_PREFIX}_draft_${workspaceId}`, [workspaceId]);
  const activityLogKey = useMemo(() => `${STORAGE_PREFIX}_activity_${workspaceId}`, [workspaceId]);
  
  // Optimized localStorage operations
  const getStoredNumber = useCallback((key: string, defaultValue: number = 0): number => {
    if (!workspaceId) return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      return stored ? parseInt(stored, 10) : defaultValue;
    } catch {
      return defaultValue;
    }
  }, [workspaceId]);
  
  const setStoredNumber = useCallback((key: string, value: number) => {
    if (!workspaceId) return;
    safeSetLocalStorage(key, value.toString());
  }, [workspaceId]);
  
  const getStoredActivityLog = useCallback((): ActivityLogEntry[] => {
    if (!workspaceId) return [];
    try {
      const stored = localStorage.getItem(activityLogKey);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      const cutoffTime = new Date(Date.now() - ACTIVITY_LOG_RETENTION_HOURS * 60 * 60 * 1000);
      
      return parsed
        .map((activity: any) => ({
          ...activity,
          timestamp: new Date(activity.timestamp)
        }))
        .filter((activity: ActivityLogEntry) => activity.timestamp > cutoffTime)
        .slice(0, ACTIVITY_LOG_MAX_ITEMS);
    } catch {
      return [];
    }
  }, [workspaceId, activityLogKey]);
  
  const setStoredActivityLog = useCallback((activities: ActivityLogEntry[], targetWorkspaceId?: string) => {
    const currentWorkspaceId = targetWorkspaceId || workspaceId;
    if (!currentWorkspaceId) return;
    
    try {
      const cutoffTime = new Date(Date.now() - ACTIVITY_LOG_RETENTION_HOURS * 60 * 60 * 1000);
      const cleanedActivities = activities
        .filter(activity => activity.timestamp > cutoffTime)
        .slice(0, ACTIVITY_LOG_MAX_ITEMS);
      
      const targetKey = `${STORAGE_PREFIX}_activity_${currentWorkspaceId}`;
      safeSetLocalStorage(targetKey, JSON.stringify(cleanedActivities));
    } catch {
      // Already handled in safeSetLocalStorage
    }
  }, [workspaceId]);
  
  // State management
  const [persistedDraftCount, setPersistedDraftCount] = useState(() => getStoredNumber(draftCountKey));
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>(getStoredActivityLog);

  
  // CRITICAL: Reset refs when workspace changes to prevent cross-workspace contamination
  const prevWorkspaceIdRef = useRef(workspaceId);
  useEffect(() => {
    if (prevWorkspaceIdRef.current !== workspaceId) {
      previousOrdersRef.current = []; // Reset to prevent cross-workspace notifications
      processedOrdersRef.current.clear(); // Clear processed order tracking
      prevWorkspaceIdRef.current = workspaceId;
    }
  }, [workspaceId]);
  
  // Calculate draft count efficiently
  const currentDraftCount = useMemo(() => {
  if (!Array.isArray(allOrders) || !workspaceId) return 0;
    
    try {
      // Filter for true draft orders - exclude very recent ones that might be transitioning
      const now = new Date();
      return allOrders.filter(order => {
        // Safety checks
        if (!order || order.status !== OrderStatus.Draft) return false;
        
        const createdAt = order.createdAt?.toDate?.() || new Date(0);
        const ageInSeconds = Math.max(0, (now.getTime() - createdAt.getTime()) / 1000);
        
        // Smart filtering: Only filter out very recent drafts that look like they're transitioning
        // Real draft orders (from public form) usually have no orderNumber and no driver
        // Transitioning orders (from create flow) will have orderNumber and often driverId assigned
        if (ageInSeconds <= 5) {
          // Multiple indicators that this is a transitioning order (not a real draft):
          // 1. Has orderNumber (assigned during confirm_order)
          // 2. Has driverId (assigned during create flow)
          const hasOrderNumber = order.orderNumber && order.orderNumber.trim() !== "";
          const hasDriverId = order.driverId && order.driverId.trim() !== "";
          
          if (hasOrderNumber || hasDriverId) {
            return false; // Filter out - likely transitioning to New
          }
          // If it's recent but no orderNumber and no driver, it's likely a real draft from public form
          return true; // Keep it - show immediately
        }
        
        return true; // Orders older than 5 seconds are always included
      }).length;
    } catch (error) {
      console.warn('Error calculating draft count:', error);
      return 0; // Fail gracefully
    }
  }, [allOrders, workspaceId]);
  
  // Update persisted count when data loads
  useEffect(() => {
    if (!isLoading && allOrders && workspaceId) {
      setPersistedDraftCount(currentDraftCount);
      setStoredNumber(draftCountKey, currentDraftCount);
    }
  }, [currentDraftCount, isLoading, allOrders, workspaceId, draftCountKey, setStoredNumber]);
  
  // Activity tracking - FIXED: Proper Draft/New handling to prevent double notifications
  // StrictMode-safe implementation with ref-based duplicate prevention
  const processedOrdersRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // Skip notification processing when page is hidden to save resources
    if (!Array.isArray(allOrders) || !workspaceId || !isPageVisible) return;
    
    const previousOrders = previousOrdersRef.current;
    if (previousOrders.length === 0) {
      previousOrdersRef.current = allOrders;
      return;
    }
    
    // Build lookup maps for efficiency
    const previousOrderMap = new Map(previousOrders.map(order => [order.id, order]));
    
    const newActivities: ActivityLogEntry[] = [];
    const timestamp = new Date();
    
    // Process order changes in batches
    allOrders.forEach(order => {
      const previousOrder = previousOrderMap.get(order.id);
      const customerName = getCustomerName(order.customer);
      
      // Order started (status changed to InProgress)
      if (order.status === OrderStatus.InProgress && previousOrder && previousOrder.status !== OrderStatus.InProgress) {
        const activityId = `order_started_${order.id}`;
        
        if (!processedOrdersRef.current.has(activityId)) {
          processedOrdersRef.current.add(activityId);
          const driverName = getDriverName(order.driverId, allDrivers);
          newActivities.push({
            id: `${activityId}_${timestamp.getTime()}`,
            type: 'order_started',
            orderId: order.id,
            orderNumber: order.orderNumber,
            customerName,
            timestamp,
            message: `${driverName} startet levering til ${customerName}`
          });
        }
      }
      
      // Order completed (status changed to Completed)
      else if (order.status === OrderStatus.Completed && previousOrder && previousOrder.status !== OrderStatus.Completed) {
        const activityId = `order_completed_${order.id}`;
        
        if (!processedOrdersRef.current.has(activityId)) {
          processedOrdersRef.current.add(activityId);
          const driverName = getDriverName(order.driverId, allDrivers);
          newActivities.push({
            id: `${activityId}_${timestamp.getTime()}`,
            type: 'order_completed',
            orderId: order.id,
            orderNumber: order.orderNumber,
            customerName,
            timestamp,
            message: `${driverName} fullførte levering til ${customerName}`
          });
        }
      }
    });
    
    // Update activity log if there are new activities
    if (newActivities.length > 0) {
      setActivityLog(prev => {
        const updated = [...newActivities, ...prev].slice(0, ACTIVITY_LOG_MAX_ITEMS);
        setStoredActivityLog(updated, workspaceId); // Pass current workspaceId explicitly
        return updated;
      });
    }
    
    previousOrdersRef.current = allOrders;
  }, [allOrders, workspaceId, isLoading, allDrivers, setStoredActivityLog, isPageVisible]);
  
  // Use persisted count during loading to prevent badge from disappearing
  const draftOrderCount = isLoading ? persistedDraftCount : currentDraftCount;
  
  const value: NotificationContextType = {
    draftOrderCount,
    activityLog
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Public provider that conditionally renders the authenticated provider.
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { isAuthenticated } = useUserSession();
  const { activeWorkspace } = useWorkspace();
  const { pathname } = useLocation();
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  // Standalone public and authentication surfaces do not need workspace
  // notifications, even when a signed-in workspace remains cached.
  const isStandaloneRoute =
    pathname.startsWith("/order/") ||
    pathname === "/order" ||
    pathname.startsWith("/shopify/") ||
    [
      "/login",
      "/logout",
      "/signup",
      "/oauth/authorize",
      "/admin-mcp/oauth/authorize",
      "/load_workspace",
      "/reset_password",
    ].some((prefix) => pathname.startsWith(prefix));

  if (isStandaloneRoute || !isAuthenticated()) {
    // Provide safe default values so hooks can be used without crashing
    const defaultValue: NotificationContextType = { draftOrderCount: 0, activityLog: [] };
    return (
      <NotificationContext.Provider value={defaultValue}>
        {children}
      </NotificationContext.Provider>
    );
  }

  return <AuthenticatedNotificationProvider key={workspaceId}>{children}</AuthenticatedNotificationProvider>;
}; 
